import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Button,
  Box,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
  Icon,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  getQBInvoice,
  updateContractThirdPartyId,
} from "../../../shared/billing.api.js";
import dayjs from "dayjs";
import {
  CallMadeIcon,
  CheckCircleIcon,
  CloseIcon,
  CustomerIcon,
  MonetizationOnIcon,
  OpenInNewWindowIcon,
  SendIcon,
  SMSIcon,
  StatusDotIcon,
} from "../../../components/mui/index.js";
import { useSingleInvoiceActions } from "./actions/single-action.component.js";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Popper from "@mui/material/Popper";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Modal from "@mui/material/Modal";
import Badge from "@mui/material/Badge";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

export const QBInvoiceDetailModal = ({
  invoiceId,
  open,
  selectedMonth,
  onClose,
  snackbarRef,
  metaData,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [manualStripeModalOpen, setManualStripeModalOpen] = useState(false);
  const [manualStripeId, setManualStripeId] = useState("");
  const [manualStripeApiResult, setManualStripeApiResult] = useState(null);
  const [manualStripeApiError, setManualStripeApiError] = useState(null);
  const [triggerFreshInvoiceFetch, setTriggerFreshInvoiceFetch] =
    useState(false);
  const [manualPandadocModalOpen, setManualPandadocModalOpen] = useState(false);
  const [manualPandadocId, setManualPandadocId] = useState("");
  const [manualPandadocApiResult, setManualPandadocApiResult] = useState(null);
  const [manualPandadocApiError, setManualPandadocApiError] = useState(null);
  const [expandedChangelogIdx, setExpandedChangelogIdx] = useState(null);

  // Use single actions hook
  const single = useSingleInvoiceActions({
    snackbarRef,
    setIsLoading,
  });

  useEffect(() => {
    fetchInvoiceDetails();
  }, [invoiceId, open, selectedMonth]);

  const fetchInvoiceDetails = () => {
    if (!invoiceId || !open) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    getQBInvoice(invoiceId, {
      start_date: dayjs(selectedMonth).startOf("month").format("YYYY-MM-DD"),
    })
      .then((data) => {
        setData(data.data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message || "Failed to load invoice");
        setLoading(false);
      });
  };

  const invoice = data?.invoice;

  const handleManualStripeIdSave = () => {
    setManualStripeApiResult(null);
    setManualStripeApiError(null);

    if (!manualStripeId.trim()) {
      setManualStripeApiError("Please enter a valid Stripe Customer ID.");
      return;
    }
    if (!data?.contract_third_party_info?.contract_id) {
      setManualStripeApiError(
        "No Contract ID found for this invoice. Cannot update Stripe Customer ID.",
      );
      return;
    }

    updateContractThirdPartyId({
      contract_id: data?.contract_third_party_info?.contract_id,
      type: "stripe",
      id: manualStripeId,
    })
      .then((result) => {
        console.log("Stripe update result:", result);
        if (result?.error) {
          setManualStripeApiResult(null);
          setManualStripeApiError(result?.error);
        } else {
          setManualStripeApiResult(result?.data);
          setManualStripeApiError(null);
        }
      })
      .catch((e) => {
        setManualStripeApiResult(null);
        setManualStripeApiError(
          e?.response?.data?.error ||
            e.message ||
            e ||
            "Failed to update Stripe Customer ID.",
        );
      });
  };

  const handleManualStripeIdClear = () => {
    setManualStripeApiResult(null);
    setManualStripeApiError(null);

    if (!data?.contract_third_party_info?.contract_id) {
      setManualStripeApiError(
        "No Contract ID found for this invoice. Cannot clear Stripe Customer ID.",
      );
      return;
    }

    const existing = data?.contract_third_party_info?.stripe_customer_id;
    const confirmed = window.confirm(
      `Clear the Stripe Customer ID${existing ? ` (${existing})` : ""}? This will set it to NULL in the database.`,
    );
    if (!confirmed) return;

    updateContractThirdPartyId({
      contract_id: data?.contract_third_party_info?.contract_id,
      type: "stripe",
      clear: true,
    })
      .then((result) => {
        if (result?.error) {
          setManualStripeApiResult(null);
          setManualStripeApiError(result?.error);
        } else {
          setManualStripeApiResult(result?.data);
          setManualStripeApiError(null);
        }
      })
      .catch((e) => {
        setManualStripeApiResult(null);
        setManualStripeApiError(
          e?.response?.data?.error ||
            e.message ||
            e ||
            "Failed to clear Stripe Customer ID.",
        );
      });
  };

  const handleManualPandadocIdSave = () => {
    setManualPandadocApiResult(null);
    setManualPandadocApiError(null);

    if (!manualPandadocId.trim()) {
      setManualPandadocApiError("Please enter a valid Pandadoc ID.");
      return;
    }
    if (!data?.contract_third_party_info?.contract_id) {
      setManualPandadocApiError(
        "No Contract ID found for this invoice. Cannot update Pandadoc ID.",
      );
      return;
    }

    updateContractThirdPartyId({
      contract_id: data?.contract_third_party_info?.contract_id,
      type: "pandadoc",
      id: manualPandadocId,
    })
      .then((result) => {
        if (result?.error) {
          setManualPandadocApiResult(null);
          setManualPandadocApiError(result.error);
        } else {
          setManualPandadocApiResult(result?.data);
          setManualPandadocApiError(null);
        }
      })
      .catch((e) => {
        setManualPandadocApiResult(null);
        setManualPandadocApiError(
          e?.response?.data?.error ||
            e.message ||
            e ||
            "Failed to update Pandadoc ID.",
        );
      });
  };

  const readyforBilling = () => {
    if (!data) {
      return null;
    }
    const readyForBilling =
      !!data?.accountName &&
      !!data?.contract_third_party_info?.stripe_customer_id;
    const complete = data?.invoice?.EmailStatus === "EmailSent";

    return (
      <>
        {complete ? (
          <Tooltip title={<Typography>Invoice Sent</Typography>}>
            <span>
              <StatusDotIcon fill={"#5d8ef8ff"} />{" "}
            </span>
          </Tooltip>
        ) : readyForBilling ? (
          <Tooltip title={<Typography>Ready for Automated Billing</Typography>}>
            <span>
              <StatusDotIcon fill={"#3bb143"} />{" "}
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            title={
              <Stack>
                <Typography>Not Ready for Automated Billing.</Typography>
                {!data.accountName && (
                  <Typography>- Missing linked Cinch Account</Typography>
                )}
                {!data?.contract_third_party_info.stripe_customer_id && (
                  <Typography>- Missing Stripe Customer ID</Typography>
                )}
              </Stack>
            }
          >
            <span>
              <StatusDotIcon fill={"#ff0000"} />{" "}
            </span>
          </Tooltip>
        )}
      </>
    );
  };

  const handleClose = () => {
    onClose(triggerFreshInvoiceFetch);
  };

  const stripeErrorCount = Array.isArray(data?.stripe_payments)
    ? data.stripe_payments.filter(
        (payment) =>
          payment.last_payment_error != null &&
          payment.created &&
          dayjs.unix(payment.created).isAfter(dayjs().subtract(32, "days")),
      ).length
    : 0;

  return (
    <Dialog
      open={open}
      onClose={(e, r) => {
        e.stopPropagation();
        if (r === "backdropClick") {
          return;
        }

        handleClose(e);
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6">Invoice Details</Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              {readyforBilling()}
            </Box>
          </Stack>
          <Box>
            <IconButton
              onClick={(e) => setMenuAnchorEl(e.currentTarget)}
              size="small"
            >
              <MoreVertIcon />
            </IconButton>
            <Popper
              open={Boolean(menuAnchorEl)}
              anchorEl={menuAnchorEl}
              placement="bottom-end"
              disablePortal
            >
              <Paper>
                <MenuList>
                  <MenuItem
                    onClick={() => {
                      setManualStripeModalOpen(true);
                      setMenuAnchorEl(null);
                    }}
                  >
                    Manually Match Stripe ID
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setManualPandadocModalOpen(true);
                      setMenuAnchorEl(null);
                    }}
                  >
                    Manually Match Pandadoc ID
                  </MenuItem>
                </MenuList>
              </Paper>
            </Popper>
            <IconButton onClick={() => handleClose()}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !invoice ? (
          <Typography>No invoice data found.</Typography>
        ) : (
          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                {" "}
                {invoice?.DocNumber ? (
                  <Typography variant="h6">
                    QB Doc Number: {invoice.DocNumber}
                  </Typography>
                ) : (
                  <Typography variant="h6">
                    QB Invoice ID: {invoice?.Id ?? "--"}
                  </Typography>
                )}
              </Box>
              <Box></Box>
            </Box>

            <Box
              sx={{
                justifyContent: "space-between",
                width: "100%",
                display: "flex",
              }}
            >
              {/*  Fields */}
              <Stack
                sx={{ maxWidth: "680px", minWidth: "0", flex: 1 }}
                spacing={2}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ width: "100%" }}
                >
                  {/* Left column: Invoice fields */}
                  <Box sx={{ maxWidth: 340, flex: 1, minWidth: 0 }}>
                    <Typography>
                      <strong>Company:</strong>{" "}
                      {invoice?.CustomerRef?.name ?? "--"}
                    </Typography>
                    <Typography>
                      <strong>Linked Account:</strong>{" "}
                      {data?.accountName ?? "--"}
                    </Typography>
                    <Typography
                      sx={{
                        color: !data?.accountId ? "error.main" : "inherit",
                        fontWeight: !data?.accountId ? "bold" : "normal",
                      }}
                    >
                      <strong>Account ID:</strong> {data?.accountId ?? "--"}
                    </Typography>
                    <Typography>
                      <strong>Created Date:</strong>{" "}
                      {invoice?.MetaData?.CreateTime
                        ? dayjs(invoice.MetaData.CreateTime).format(
                            "MMM D, YYYY",
                          )
                        : "--"}
                    </Typography>
                    <Typography>
                      <strong>Invoice Date:</strong>{" "}
                      {invoice?.TxnDate
                        ? dayjs(invoice.TxnDate).format("MMM D, YYYY")
                        : "--"}
                    </Typography>
                    <Typography>
                      <strong>Status:</strong> {invoice?.EmailStatus ?? "--"}
                    </Typography>
                    <Typography>
                      <strong>E-Invoice Status:</strong>{" "}
                      {invoice?.EInvoiceStatus ?? "--"}
                    </Typography>

                    <Typography>
                      <strong>Total Invoice Amount:</strong>{" "}
                      {typeof invoice?.TotalAmt === "number"
                        ? `$${invoice.TotalAmt.toFixed(2)}`
                        : "--"}
                    </Typography>

                    <Typography>
                      <strong>Customer Balance:</strong>{" "}
                      {typeof data?.quickbooks_customer?.Balance === "number"
                        ? `$${data.quickbooks_customer.Balance.toFixed(2)}`
                        : "--"}
                    </Typography>
                  </Box>

                  {/* Middle column: Other fields (Stripe, Account, etc.) */}
                  <Box sx={{ maxWidth: 340, flex: 1, minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={2}
                      flexWrap="wrap"
                      alignItems="center"
                    >
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            data?.contract_third_party_info?.stripe_customer_id
                          ) {
                            window.open(
                              `https://dashboard.stripe.com/customers/${data.contract_third_party_info.stripe_customer_id}`,
                              "_blank",
                            );
                          }
                        }}
                      >
                        <Typography
                          sx={{
                            color: !data?.contract_third_party_info
                              ?.stripe_customer_id
                              ? "error.main"
                              : "inherit",
                            fontWeight: !data?.contract_third_party_info
                              ?.stripe_customer_id
                              ? "bold"
                              : "normal",
                          }}
                        >
                          <strong>Stripe Customer ID:</strong>{" "}
                          {data?.contract_third_party_info
                            ?.stripe_customer_id ? (
                            <a
                              href={`https://dashboard.stripe.com/customers/${data.contract_third_party_info.stripe_customer_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textDecoration: "underline",
                                color: "#1976d2",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {
                                data.contract_third_party_info
                                  .stripe_customer_id
                              }
                            </a>
                          ) : (
                            "--"
                          )}
                        </Typography>
                      </Box>
                    </Stack>

                    {data?.contract_third_party_info
                      ?.stripe_customer_business_name && (
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Typography>
                          <strong>Stripe Customer Business Name:</strong>{" "}
                          {
                            data.contract_third_party_info
                              .stripe_customer_business_name
                          }
                        </Typography>
                      </Stack>
                    )}

                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          if (invoice?.Id) {
                            window.open(
                              `https://qbo.intuit.com/app/invoice?txnId=${invoice.Id}`,
                              "_blank",
                            );
                          }
                        }}
                        sx={{ cursor: invoice?.Id ? "pointer" : "default" }}
                      >
                        <Typography>
                          <strong>QB Invoice ID:</strong>{" "}
                          {invoice?.Id ? (
                            <a
                              href={`https://qbo.intuit.com/app/invoice?txnId=${invoice.Id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textDecoration: "underline",
                                color: "#1976d2",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {invoice.Id}
                            </a>
                          ) : (
                            "--"
                          )}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          if (data?.invoice.CustomerRef.value) {
                            window.open(
                              `https://qbo.intuit.com/app/customerdetail?nameId=${data.invoice.CustomerRef.value}`,
                              "_blank",
                            );
                          }
                        }}
                        sx={{
                          cursor: data?.invoice?.CustomerRef?.value
                            ? "pointer"
                            : "default",
                        }}
                      >
                        <Typography>
                          <strong>QB Customer ID:</strong>{" "}
                          {data?.invoice?.CustomerRef?.value ? (
                            <a
                              href={`https://qbo.intuit.com/app/customerdetail?nameId=${data.invoice.CustomerRef.value}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textDecoration: "underline",
                                color: "#1976d2",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {data.invoice.CustomerRef.value}
                            </a>
                          ) : (
                            "--"
                          )}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          if (data?.contract_third_party_info?.pandadoc_id) {
                            window.open(
                              `https://app.pandadoc.com/a/#/documents/${data.contract_third_party_info.pandadoc_id}`,
                              "_blank",
                            );
                          }
                        }}
                        sx={{
                          cursor: data?.contract_third_party_info?.pandadoc_id
                            ? "pointer"
                            : "default",
                        }}
                      >
                        <Typography>
                          <strong>Pandadoc:</strong>{" "}
                          {data?.contract_third_party_info.pandadoc_id ? (
                            <a
                              href={`https://app.pandadoc.com/a/#/documents/${data.contract_third_party_info.pandadoc_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textDecoration: "underline",
                                color: "#1976d2",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {data.contract_third_party_info.pandadoc_id}
                            </a>
                          ) : (
                            "--"
                          )}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            data?.contract_third_party_info?.hubspot_deal_id
                          ) {
                            window.open(
                              `https://app.hubspot.com/contacts/21771408/record/0-3/${data.contract_third_party_info.hubspot_deal_id}`,
                              "_blank",
                            );
                          }
                        }}
                        sx={{
                          cursor: data?.contract_third_party_info
                            ?.hubspot_deal_id
                            ? "pointer"
                            : "default",
                        }}
                      >
                        <Typography>
                          <strong>Hubspot:</strong>{" "}
                          {data?.contract_third_party_info.hubspot_deal_id ? (
                            <a
                              href={`https://app.hubspot.com/contacts/21771408/record/0-3/${data.contract_third_party_info.hubspot_deal_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textDecoration: "underline",
                                color: "#1976d2",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {data.contract_third_party_info.hubspot_deal_id}
                            </a>
                          ) : (
                            "--"
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                    <Typography
                      sx={{
                        color: !data?.contract_third_party_info?.contract_id
                          ? "error.main"
                          : "inherit",
                        fontWeight: !data?.contract_third_party_info
                          ?.contract_id
                          ? "bold"
                          : "normal",
                      }}
                    >
                      <strong>Cinch Contract ID:</strong>{" "}
                      {data?.contract_third_party_info?.contract_id ?? "--"}
                    </Typography>

                    <Typography
                      sx={{
                        color:
                          data.contract_payment_type !== "CC"
                            ? "error.main"
                            : "inherit",
                      }}
                    >
                      <strong>Contract Payment Type:</strong>{" "}
                      {data.contract_payment_type ?? "--"}
                    </Typography>

                    <Typography>
                      <strong>Due Date:</strong>{" "}
                      {invoice?.DueDate
                        ? dayjs(invoice.DueDate).format("MMM D, YYYY")
                        : "--"}
                    </Typography>
                    <Typography>
                      <strong>Invoice Balance:</strong>{" "}
                      {typeof invoice?.Balance === "number"
                        ? `$${invoice.Balance.toFixed(2)}`
                        : "--"}
                    </Typography>
                  </Box>
                </Stack>
                <Divider />

                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                  sx={{ width: "100%" }}
                >
                  {/* Left column: Invoice fields */}
                  <Box sx={{ maxWidth: 340, flex: 1, minWidth: 0 }}>
                    <Typography>
                      <strong>
                        {metaData?.dates?.overage_month
                          ? `${dayjs(metaData.dates.overage_month).format(
                              "MMM YYYY",
                            )} SMS Overage:`
                          : "SMS Overage:"}
                      </strong>
                      {typeof data?.overages_and_usage?.usage_sms_credits
                        ?.current?.overage_usage === "number"
                        ? data.overages_and_usage.usage_sms_credits.current
                            .overage_usage
                        : "--"}
                    </Typography>
                    <Typography>
                      <strong>
                        {metaData?.dates?.overage_month
                          ? `${dayjs(metaData.dates.overage_month).format(
                              "MMM YYYY",
                            )} SMS Overage Unit Cost:`
                          : "SMS Overage Unit Cost:"}
                      </strong>{" "}
                      {typeof data?.overages_and_usage?.usage_sms_credits
                        ?.current?.overage_per_unit === "number"
                        ? `$${data.overages_and_usage.usage_sms_credits.current.overage_per_unit.toFixed(
                            2,
                          )}`
                        : "--"}
                    </Typography>
                    <Box sx={{ my: 1 }} />
                    <Typography>
                      <strong>
                        {metaData?.dates?.overage_last_month
                          ? `${dayjs(metaData.dates.overage_last_month).format(
                              "MMM YYYY",
                            )} SMS Overage:`
                          : "Last Month SMS Overage:"}
                      </strong>
                      {typeof data?.overages_and_usage?.usage_sms_credits
                        ?.last_month?.overage_usage === "number"
                        ? data?.overages_and_usage.usage_sms_credits.last_month
                            .overage_usage
                        : "--"}
                    </Typography>
                    <Typography>
                      <strong>
                        {metaData?.dates?.overage_last_month
                          ? `${dayjs(metaData.dates.overage_last_month).format(
                              "MMM YYYY",
                            )} SMS Overage Unit Cost:`
                          : "Last Month SMS Overage Unit Cost:"}
                      </strong>{" "}
                      {typeof data?.overages_and_usage?.usage_sms_credits
                        ?.last_month?.overage_per_unit === "number"
                        ? `$${data?.overages_and_usage.usage_sms_credits.last_month.overage_per_unit.toFixed(
                            2,
                          )}`
                        : "--"}
                    </Typography>
                  </Box>
                  {/* Middle column: Other fields (Stripe, Account, etc.) */}
                  <Box sx={{ maxWidth: 340, flex: 1, minWidth: 0 }}>
                    <Typography>
                      <strong>
                        {metaData?.dates?.overage_month
                          ? `${dayjs(metaData.dates.overage_month).format(
                              "MMM YYYY",
                            )} Unbilled Blocks:`
                          : "This Month Contact Overage - Unbilled Blocks:"}
                      </strong>
                      {data?.overages_and_usage?.additional_usage_base_contacts
                        ?.current?.overage_usage ?? "--"}
                    </Typography>

                    <Typography>
                      <strong>
                        {metaData?.dates?.overage_month
                          ? `${dayjs(metaData.dates.overage_month).format(
                              "MMM YYYY",
                            )} Ten K More:`
                          : "This Month Contact Overage - Ten K More:"}
                      </strong>
                      {data?.overages_and_usage?.additional_usage_base_contacts
                        ?.current?.overage_per_unit ?? "--"}
                    </Typography>

                    <Typography>
                      <strong>
                        {metaData?.dates?.overage_month
                          ? `${dayjs(metaData.dates.overage_month).format(
                              "MMM YYYY",
                            )} Unbilled Dollars:`
                          : "This Month Overage Unbilled Dollars:"}
                      </strong>{" "}
                      {Number(data?.overages_and_usage?.additional_usage_base_contacts?.current?.overage_usage) &&
                      Number(data?.overages_and_usage?.additional_usage_base_contacts?.current?.overage_per_unit)
                        ? `$${(
                            Number(data.overages_and_usage.additional_usage_base_contacts.current.overage_usage) *
                            Number(data.overages_and_usage.additional_usage_base_contacts.current.overage_per_unit)
                          ).toFixed(2)}`
                        : "--"}
                    </Typography>
                    {/*
                    <Box sx={{ my: 1 }} />
                    <Typography>
                      <strong>
                        {metaData?.dates?.overage_last_month
                          ? `${dayjs(metaData.dates.overage_last_month).format(
                              "MMM YYYY",
                            )} Contact Overage:`
                          : "Last Month Contact Overage:"}
                      </strong>
                      {typeof data?.overages_and_usage
                        ?.additional_usage_base_contacts?.last_month
                        ?.overage_usage === "number"
                        ? data?.overages_and_usage
                            .additional_usage_base_contacts.last_month
                            .overage_usage
                        : "--"}
                    </Typography>
                    <Typography>
                      <strong>
                        {metaData?.dates?.overage_last_month
                          ? `${dayjs(metaData.dates.overage_last_month).format(
                              "MMM YYYY",
                            )} Contact Overage Unit Cost:`
                          : "Last Month Contact Overage Unit Cost:"}
                      </strong>{" "}
                      {typeof data?.overages_and_usage
                        ?.additional_usage_base_contacts?.last_month
                        ?.overage_per_unit === "number"
                        ? `$${data?.overages_and_usage.additional_usage_base_contacts.last_month.overage_per_unit.toFixed(
                            2,
                          )}`
                        : "--"}
                    </Typography>
                    */}
                  </Box>
                </Stack>
              </Stack>

              {/* Actions */}
              <Stack>
                {/* Right column: Action buttons */}
                <Box sx={{ maxWidth: 340, flex: 1, minWidth: 0 }}>
                  <Stack direction="column" spacing={2} alignItems="flex-start">
                    <Box
                      sx={{
                        width: "100%",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 2,
                        p: 2,
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Actions
                      </Typography>
                      <Stack
                        direction="column"
                        spacing={2}
                        alignItems="flex-start"
                      >
                        {/* Add SMS Overage */}
                        <Tooltip
                          placement="right"
                          title={
                            <Typography>
                              {!data.accountId
                                ? `Please link an account to QuickBooks Invoice ${invoice?.Id} before updating SMS overage`
                                : "Add SMS Overage"}
                            </Typography>
                          }
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                              cursor: "pointer",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              "&:hover": { bgcolor: "action.hover" },
                              width: "100%",
                              minWidth: 180,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!data.accountId) {
                                snackbarRef.current?.addSnackbar({
                                  type: "error",
                                  message: `Please link an account to QuickBooks Invoice ${invoice?.Id} before updating SMS overage`,
                                });
                                return;
                              }
                              single.openSmsOverageModal(data, (result) => {
                                if (result) {
                                  setTriggerFreshInvoiceFetch(true);
                                  fetchInvoiceDetails();
                                }
                              });
                            }}
                            tabIndex={0}
                            role="button"
                          >
                            <SMSIcon />
                            <Typography>Add SMS Overage</Typography>
                          </Stack>
                        </Tooltip>
                        {/* Add Customer Count Overage */}
                        <Tooltip
                          placement="right"
                          title={
                            <Typography>
                              {!data.accountId
                                ? `Please link an account to QuickBooks Invoice ${invoice?.Id} before updating Customer Count overage`
                                : "Add Customer Count Overage"}
                            </Typography>
                          }
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                              cursor: "pointer",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              "&:hover": { bgcolor: "action.hover" },
                              width: "100%",
                              minWidth: 180,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!data.accountId) {
                                snackbarRef.current?.addSnackbar({
                                  type: "error",
                                  message: `Please link an account to QuickBooks Invoice ${invoice?.Id} before updating Customer Count overage`,
                                });
                                return;
                              }
                              single.openCustomerCountOverageModal(
                                data,
                                (result) => {
                                  if (result) {
                                    setTriggerFreshInvoiceFetch(true);
                                    fetchInvoiceDetails();
                                  }
                                },
                              );
                            }}
                            tabIndex={0}
                            role="button"
                          >
                            <CustomerIcon />
                            <Typography>Add Customer Count Overage</Typography>
                          </Stack>
                        </Tooltip>
                        <Box sx={{ width: "100%" }}>
                          <Divider />
                        </Box>
                        {/* Charge Stripe Customer */}
                        <Tooltip
                          placement="right"
                          title={
                            <Typography>
                              {!data.accountId
                                ? `Please link an account to QuickBooks Invoice ${invoice?.Id} before charging customer`
                                : !data?.contract_third_party_info
                                      ?.stripe_customer_id
                                  ? `No Stripe Customer ID found for QuickBooks Invoice ${invoice?.Id}. Please ensure the linked account has a valid Stripe Customer ID before charging.`
                                  : "Charge Stripe Customer"}
                            </Typography>
                          }
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                              cursor: "pointer",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              "&:hover": { bgcolor: "action.hover" },
                              width: "100%",
                              minWidth: 180,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!data.accountId) {
                                snackbarRef.current?.addSnackbar({
                                  type: "error",
                                  message: `Please link an account to QuickBooks Invoice ${invoice?.Id} before charging customer`,
                                });
                                return;
                              }
                              if (
                                !data?.contract_third_party_info
                                  ?.stripe_customer_id
                              ) {
                                snackbarRef.current?.addSnackbar({
                                  type: "error",
                                  message: `No Stripe Customer ID found for QuickBooks Invoice ${invoice?.Id}. Please ensure the linked account has a valid Stripe Customer ID before charging.`,
                                });
                                return;
                              }
                              single.openChargeCustomerModal(data, (result) => {
                                if (result) {
                                  setTriggerFreshInvoiceFetch(true);
                                  fetchInvoiceDetails();
                                }
                              });
                            }}
                            tabIndex={0}
                            role="button"
                          >
                            <MonetizationOnIcon />
                            <Typography>Charge Stripe Customer</Typography>
                          </Stack>
                        </Tooltip>
                        {/* Mark as Paid */}
                        <Tooltip
                          placement="right"
                          title={<Typography>Mark as Paid</Typography>}
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                              cursor: "pointer",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              "&:hover": { bgcolor: "action.hover" },
                              width: "100%",
                              minWidth: 180,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              single.openMarkPaidModal(data, (result) => {
                                if (result) {
                                  setTriggerFreshInvoiceFetch(true);
                                  fetchInvoiceDetails();
                                }
                              });
                            }}
                            tabIndex={0}
                            role="button"
                          >
                            <CheckCircleIcon />
                            <Typography>Mark as Paid</Typography>
                          </Stack>
                        </Tooltip>
                        {/* Send Invoice to Customer */}
                        <Tooltip
                          placement="right"
                          title={
                            <Typography>
                              {!data.accountId
                                ? `Please link an account to QuickBooks Invoice ${invoice?.Id} before sending to customer`
                                : invoice.EmailStatus === "EmailSent" &&
                                    data.changelogs?.find(
                                      (log) =>
                                        log.result_status === "success" &&
                                        log.action.includes(
                                          "send_customer_invoice",
                                        ),
                                    )
                                  ? `Invoice ${invoice?.Id} already has been sent to the customer. Not sending again.`
                                  : "Send Invoice to Customer"}
                            </Typography>
                          }
                        >
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.5}
                            sx={{
                              cursor: "pointer",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              "&:hover": { bgcolor: "action.hover" },
                              width: "100%",
                              minWidth: 180,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!data.accountId) {
                                snackbarRef.current?.addSnackbar({
                                  type: "error",
                                  message: `Please link an account to QuickBooks Invoice ${invoice?.Id} before sending to customer`,
                                });
                                return;
                              }
                              if (
                                invoice.EmailStatus === "EmailSent" &&
                                data.changelogs?.find(
                                  (log) =>
                                    log.result_status === "success" &&
                                    log.action.includes(
                                      "send_customer_invoice",
                                    ),
                                )
                              ) {
                                snackbarRef.current?.addSnackbar({
                                  type: "info",
                                  message: `Invoice ${invoice?.Id} already has been sent to the customer. Not sending again.`,
                                });
                                return;
                              }
                              single.openSendInvoiceModal(data, (result) => {
                                if (result) {
                                  setTriggerFreshInvoiceFetch(true);
                                  fetchInvoiceDetails();
                                }
                              });
                            }}
                            tabIndex={0}
                            role="button"
                          >
                            <SendIcon />
                            <Typography>Send Invoice to Customer</Typography>
                          </Stack>
                        </Tooltip>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Box>

            <Divider />

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Overages/Usage
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                  {data?.overages_and_usage ? (
                    <Stack spacing={1}>
                      {Object.entries(data.overages_and_usage).map(
                        ([key, value]) => (
                          <Box key={key}>
                            <Typography variant="body2">
                              <strong>{key}:</strong>{" "}
                              {typeof value === "object" && value !== null ? (
                                <pre
                                  style={{
                                    margin: 0,
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {JSON.stringify(value, null, 2)}
                                </pre>
                              ) : (
                                String(value)
                              )}
                            </Typography>
                          </Box>
                        ),
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2">
                      No overages or usage data found.
                    </Typography>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>

            <Divider />

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Line Items
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                  <Stack spacing={2}>
                    {invoice?.Line?.map((line, idx) => (
                      <Box
                        key={line.Id || idx}
                        sx={{ borderBottom: "1px solid #eee", pb: 1 }}
                      >
                        <Typography>
                          <strong>Line Number:</strong> {line.LineNum ?? "--"}
                        </Typography>
                        <Typography>
                          <strong>Description:</strong>{" "}
                          {line.Description ?? "--"}
                        </Typography>
                        <Typography>
                          <strong>Amount:</strong>{" "}
                          {typeof line.Amount === "number"
                            ? `$${line.Amount.toFixed(2)}`
                            : "--"}
                        </Typography>
                        <Typography>
                          <strong>Detail Type:</strong>{" "}
                          {line.DetailType ?? "--"}
                        </Typography>
                        {line.SalesItemLineDetail && (
                          <>
                            <Typography>
                              <strong>Item:</strong>{" "}
                              {line.SalesItemLineDetail.ItemRef?.name ?? "--"}
                            </Typography>
                            <Typography>
                              <strong>Qty:</strong>{" "}
                              {line.SalesItemLineDetail.Qty ?? "--"}
                            </Typography>
                            <Typography>
                              <strong>Unit Price:</strong>{" "}
                              {typeof line.SalesItemLineDetail.UnitPrice ===
                              "number"
                                ? `$${line.SalesItemLineDetail.UnitPrice.toFixed(
                                    2,
                                  )}`
                                : "--"}
                            </Typography>
                          </>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Divider />

            {Array.isArray(data?.stripe_payments) &&
              data.stripe_payments?.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "bold" }}
                      >
                        Stripe Payments
                      </Typography>
                      <Badge
                        color="error"
                        badgeContent={
                          stripeErrorCount > 0 ? stripeErrorCount : undefined
                        }
                        sx={{ ml: "30px" }}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ maxHeight: 300, overflowY: "auto", mb: 2 }}>
                      <Stack spacing={2}>
                        {data.stripe_payments.map((payment, idx) => (
                          <Box
                            key={payment.id || idx}
                            sx={{ borderBottom: "1px solid #eee", pb: 1 }}
                          >
                            <Typography variant="body2">
                              <strong>ID:</strong> {payment.id ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Amount:</strong>{" "}
                              {typeof payment.amount === "number"
                                ? `$${(payment.amount / 100).toFixed(2)}`
                                : "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Currency:</strong>{" "}
                              {payment.currency ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Description:</strong>{" "}
                              {payment.description ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Status:</strong> {payment.status ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Created:</strong>{" "}
                              {payment.created
                                ? dayjs(payment.created * 1000).format(
                                    "MMM D, YYYY h:mma",
                                  )
                                : "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Payment Method:</strong>{" "}
                              {payment.payment_method ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Last payment has error?:</strong>{" "}
                              {payment.last_payment_error ?? "--"}
                            </Typography>

                            <Typography variant="body2">
                              <strong>Customer:</strong>{" "}
                              {payment.customer ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Receipt Email:</strong>{" "}
                              {payment.receipt_email ?? "--"}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

            <Divider />
            {Array.isArray(data?.qb_payments) &&
              data.qb_payments?.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      QuickBooks Payments
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ maxHeight: 300, overflowY: "auto", mb: 2 }}>
                      <Stack spacing={2}>
                        {data.qb_payments.map((payment, idx) => (
                          <Box
                            key={payment.Id || idx}
                            sx={{ borderBottom: "1px solid #eee", pb: 1 }}
                          >
                            <Typography variant="body2">
                              <strong>ID:</strong> {payment.Id ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Ref Num:</strong>{" "}
                              {payment.PaymentRefNum ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Total Amount:</strong>{" "}
                              {typeof payment.TotalAmt === "number"
                                ? `$${payment.TotalAmt.toFixed(2)}`
                                : "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Unapplied Amount:</strong>{" "}
                              {typeof payment.UnappliedAmt === "number"
                                ? `$${payment.UnappliedAmt.toFixed(2)}`
                                : "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Txn Date:</strong>{" "}
                              {payment.TxnDate ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Status:</strong>{" "}
                              {payment.TxnStatus ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Customer:</strong>{" "}
                              {payment.CustomerRef?.name ??
                                payment.CustomerRef?.value ??
                                "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Currency:</strong>{" "}
                              {payment.CurrencyRef?.name ??
                                payment.CurrencyRef?.value ??
                                "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Payment Method:</strong>{" "}
                              {payment.PaymentMethodRef?.name ??
                                payment.PaymentMethodRef?.value ??
                                "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Deposit To Account:</strong>{" "}
                              {payment.DepositToAccountRef?.name ??
                                payment.DepositToAccountRef?.value ??
                                "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Exchange Rate:</strong>{" "}
                              {typeof payment.ExchangeRate === "number"
                                ? payment.ExchangeRate
                                : "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Private Note:</strong>{" "}
                              {payment.PrivateNote ?? "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Created:</strong>{" "}
                              {payment.MetaData?.CreateTime
                                ? dayjs(payment.MetaData.CreateTime).format(
                                    "MMM D, YYYY h:mma",
                                  )
                                : "--"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Last Updated:</strong>{" "}
                              {payment.MetaData?.LastUpdatedTime
                                ? dayjs(
                                    payment.MetaData.LastUpdatedTime,
                                  ).format("MMM D, YYYY h:mma")
                                : "--"}
                            </Typography>
                            {/* Payment Lines */}
                            {Array.isArray(payment.Line) &&
                              payment.Line?.length > 0 && (
                                <Box sx={{ mt: 1, mb: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: "bold" }}
                                  >
                                    Payment Lines:
                                  </Typography>
                                  {payment.Line.map((line, lineIdx) => (
                                    <Box key={lineIdx} sx={{ ml: 2, mb: 1 }}>
                                      <Typography variant="body2">
                                        <strong>Amount:</strong>{" "}
                                        {typeof line.Amount === "number"
                                          ? `$${line.Amount.toFixed(2)}`
                                          : "--"}
                                      </Typography>
                                      {/* Linked Transactions */}
                                      {Array.isArray(line.LinkedTxn) &&
                                        line.LinkedTxn?.length > 0 && (
                                          <Box sx={{ ml: 2 }}>
                                            <Typography
                                              variant="body2"
                                              sx={{ fontWeight: "bold" }}
                                            >
                                              Linked Transactions:
                                            </Typography>
                                            {line.LinkedTxn.map(
                                              (txn, txnIdx) => (
                                                <Typography
                                                  variant="body2"
                                                  key={txnIdx}
                                                >
                                                  <strong>TxnId:</strong>{" "}
                                                  {txn.TxnId ?? "--"}{" "}
                                                  <strong>TxnType:</strong>{" "}
                                                  {txn.TxnType ?? "--"}{" "}
                                                  <strong>TxnLineId:</strong>{" "}
                                                  {txn.TxnLineId ?? "--"}
                                                </Typography>
                                              ),
                                            )}
                                          </Box>
                                        )}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            <Divider />
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Changelogs for this Invoice
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                  <Stack spacing={2}>
                    {Array.isArray(data?.changelogs) &&
                    data.changelogs?.length > 0 ? (
                      data.changelogs.map((log, idx) => (
                        <Box
                          key={log.id || idx}
                          sx={{
                            borderBottom: "1px solid #eee",
                            pb: 1,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setExpandedChangelogIdx(
                              expandedChangelogIdx === `changelog-${idx}`
                                ? null
                                : `changelog-${idx}`,
                            )
                          }
                        >
                          <Typography variant="body2">
                            {log.result_status === "error" ? "❌" : "✅"}{" "}
                            {log.created_when
                              ? dayjs(log.created_when).format(
                                  "MMM D, YYYY h:mma",
                                )
                              : "--"}
                          </Typography>
                          <Typography variant="body2">{log.action}</Typography>
                          <Typography variant="body2">
                            {log.changed_fields
                              ? JSON.stringify(log.changed_fields)
                              : ""}
                          </Typography>
                          <Typography variant="body2">
                            {log.result_message}
                          </Typography>
                          {expandedChangelogIdx === `changelog-${idx}` && (
                            <pre
                              style={{
                                margin: 0,
                                fontFamily: "monospace",
                                whiteSpace: "pre-wrap",
                                background: "#f7f7f7",
                                borderRadius: 4,
                                padding: 8,
                                marginTop: 8,
                              }}
                            >
                              {JSON.stringify(log, null, 2)}
                            </pre>
                          )}
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2">
                        No changelogs found.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </AccordionDetails>
            </Accordion>

            <Divider />

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  Changelog History (Shows logs from previous months, up to 50)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                  <Stack spacing={2}>
                    {Array.isArray(data?.changelogs_history) &&
                    data.changelogs_history.length > 0 ? (
                      data.changelogs_history.map((log, idx) => (
                        <Box
                          key={log.id || idx}
                          sx={{
                            borderBottom: "1px solid #eee",
                            pb: 1,
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            setExpandedChangelogIdx(
                              expandedChangelogIdx === idx ? null : idx,
                            )
                          }
                        >
                          <Typography variant="body2">
                            {log.result_status === "error" ? "❌" : "✅"}{" "}
                            {log.created_when
                              ? dayjs(log.created_when).format(
                                  "MMM D, YYYY h:mma",
                                )
                              : "--"}
                          </Typography>
                          <Typography variant="body2">{log.action}</Typography>
                          <Typography variant="body2">
                            {log.changed_fields
                              ? JSON.stringify(log.changed_fields)
                              : ""}
                          </Typography>
                          <Typography variant="body2">
                            {log.result_message}
                          </Typography>
                          {expandedChangelogIdx === idx && (
                            <pre
                              style={{
                                margin: 0,
                                fontFamily: "monospace",
                                whiteSpace: "pre-wrap",
                                background: "#f7f7f7",
                                borderRadius: 4,
                                padding: 8,
                                marginTop: 8,
                              }}
                            >
                              {JSON.stringify(log, null, 2)}
                            </pre>
                          )}
                        </Box>
                      ))
                    ) : (
                      <Typography variant="body2">
                        No changelogs found.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={(e) => {
            handleClose();
          }}
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>

      {/* Manual Stripe ID Modal */}
      <Modal
        open={manualStripeModalOpen}
        onClose={(e, r) => {
          e.stopPropagation();
          if (r === "backdropClick") {
            return;
          }
          setManualStripeModalOpen(false);
          setManualStripeApiResult(null);
          setManualStripeApiError(null);
          setManualStripeId("");
        }}
        aria-labelledby="manual-stripe-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            minWidth: 320,
            maxWidth: 400,
          }}
        >
          <Typography
            id="manual-stripe-modal-title"
            variant="h6"
            sx={{ mb: 2 }}
          >
            Manually Match Stripe ID
          </Typography>
          {/* Show result if successful */}
          {manualStripeApiResult && !!manualStripeApiResult?.success ? (
            <Box>
              <Typography
                color="success.main"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                {manualStripeApiResult.cleared
                  ? "Stripe Customer ID cleared successfully!"
                  : "Stripe Customer ID updated successfully!"}
              </Typography>
              {!manualStripeApiResult.cleared && (
                <>
                  <Typography sx={{ mb: 1 }}>
                    <strong>Stripe Customer ID:</strong>{" "}
                    {manualStripeApiResult.stripe_customer_id}
                  </Typography>
                  <Typography sx={{ mb: 2 }}>
                    <strong>Description:</strong>{" "}
                    {manualStripeApiResult.stripe_customer_description}
                  </Typography>
                </>
              )}
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => {
                    setManualStripeModalOpen(false);
                    setManualStripeApiResult(null);
                    setManualStripeApiError(null);
                    setManualStripeId("");
                    fetchInvoiceDetails();
                    setTriggerFreshInvoiceFetch(true);
                  }}
                  variant="contained"
                >
                  Close
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {/* Show error if present */}
              {manualStripeApiError && (
                <Box
                  sx={{
                    mb: 2,
                    maxHeight: 120,
                    maxWidth: 400,
                    overflow: "auto",
                    wordBreak: "break-word",
                  }}
                >
                  <Typography
                    color="error.main"
                    sx={{ fontWeight: "bold", whiteSpace: "pre-wrap" }}
                  >
                    {typeof manualStripeApiError === "string"
                      ? manualStripeApiError
                      : JSON.stringify(manualStripeApiError, null, 2)}
                  </Typography>
                </Box>
              )}
              {/* Show warnings and input if not successful */}
              {!data?.contract_third_party_info?.contract_id && (
                <Box sx={{ mb: 2 }}>
                  <Typography color="error.main" sx={{ fontWeight: "bold" }}>
                    Warning: No Contract ID found. You must link an account
                    before matching a Stripe ID.
                  </Typography>
                </Box>
              )}
              {data?.contract_third_party_info?.stripe_customer_id && (
                <Box sx={{ mb: 2 }}>
                  <Typography color="warning.main" sx={{ fontWeight: "bold" }}>
                    Warning: This will overwrite the existing Stripe Customer
                    ID:
                  </Typography>
                  <Typography sx={{ mb: 1 }}>
                    <strong>Current Stripe Customer ID:</strong>{" "}
                    {data.contract_third_party_info.stripe_customer_id}
                  </Typography>
                  {data?.contract_third_party_info
                    ?.stripe_customer_description && (
                    <Typography sx={{ mb: 1 }}>
                      <strong>Description:</strong>{" "}
                      {
                        data.contract_third_party_info
                          .stripe_customer_description
                      }
                    </Typography>
                  )}
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleManualStripeIdClear}
                    disabled={!data?.contract_third_party_info?.contract_id}
                    sx={{ mt: 1 }}
                  >
                    Clear Stripe ID (set to NULL)
                  </Button>
                </Box>
              )}
              <TextField
                label="Stripe Customer ID"
                fullWidth
                value={manualStripeId}
                onChange={(e) => setManualStripeId(e.target.value)}
                sx={{ mb: 2 }}
                disabled={!data?.contract_third_party_info?.contract_id}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  onClick={() => {
                    setManualStripeModalOpen(false);
                    setManualStripeApiResult(null);
                    setManualStripeApiError(null);
                    setManualStripeId("");
                  }}
                  variant="outlined"
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={handleManualStripeIdSave}
                  disabled={
                    !manualStripeId ||
                    !data?.contract_third_party_info?.contract_id
                  }
                >
                  Save
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      {/* Manual Pandadoc ID Modal */}
      <Modal
        open={manualPandadocModalOpen}
        onClose={(e, r) => {
          e.stopPropagation();
          if (r === "backdropClick") {
            return;
          }
          setManualPandadocModalOpen(false);
          setManualPandadocApiResult(null);
          setManualPandadocApiError(null);
          setManualPandadocId("");
        }}
        aria-labelledby="manual-pandadoc-modal-title"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            minWidth: 320,
            maxWidth: 400,
          }}
        >
          <Typography
            id="manual-pandadoc-modal-title"
            variant="h6"
            sx={{ mb: 2 }}
          >
            Manually Match Pandadoc ID
          </Typography>
          {/* Show result if successful */}
          {manualPandadocApiResult && !!manualPandadocApiResult?.success ? (
            <Box>
              <Typography
                color="success.main"
                sx={{ fontWeight: "bold", mb: 2 }}
              >
                Pandadoc ID updated successfully!
              </Typography>
              <Typography sx={{ mb: 1 }}>
                <strong>Pandadoc ID:</strong>{" "}
                {manualPandadocApiResult.pandadoc_id}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  onClick={() => {
                    setManualPandadocModalOpen(false);
                    setManualPandadocApiResult(null);
                    setManualPandadocApiError(null);
                    setManualPandadocId("");
                    fetchInvoiceDetails();
                    setTriggerFreshInvoiceFetch(true);
                  }}
                  variant="contained"
                >
                  Close
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              {/* Show error if present */}
              {manualPandadocApiError && (
                <Box
                  sx={{
                    mb: 2,
                    maxHeight: 120,
                    maxWidth: 400,
                    overflow: "auto",
                    wordBreak: "break-word",
                  }}
                >
                  <Typography
                    color="error.main"
                    sx={{ fontWeight: "bold", whiteSpace: "pre-wrap" }}
                  >
                    {typeof manualPandadocApiError === "string"
                      ? manualPandadocApiError
                      : JSON.stringify(manualPandadocApiError, null, 2)}
                  </Typography>
                </Box>
              )}
              {/* Show warnings and input if not successful */}
              {!data?.contract_third_party_info?.contract_id && (
                <Box sx={{ mb: 2 }}>
                  <Typography color="error.main" sx={{ fontWeight: "bold" }}>
                    Warning: No Contract ID found. You must link an account
                    before matching a Pandadoc ID.
                  </Typography>
                </Box>
              )}
              {data?.contract_third_party_info?.pandadoc_id && (
                <Box sx={{ mb: 2 }}>
                  <Typography color="warning.main" sx={{ fontWeight: "bold" }}>
                    Warning: This will overwrite the existing Pandadoc ID:
                  </Typography>
                  <Typography sx={{ mb: 1 }}>
                    <strong>Current Pandadoc ID:</strong>{" "}
                    {data.contract_third_party_info.pandadoc_id}
                  </Typography>
                </Box>
              )}
              <TextField
                label="Pandadoc ID"
                fullWidth
                value={manualPandadocId}
                onChange={(e) => setManualPandadocId(e.target.value)}
                sx={{ mb: 2 }}
                disabled={!data?.contract_third_party_info?.contract_id}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  onClick={() => {
                    setManualPandadocModalOpen(false);
                    setManualPandadocApiResult(null);
                    setManualPandadocApiError(null);
                    setManualPandadocId("");
                  }}
                  variant="outlined"
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={handleManualPandadocIdSave}
                  disabled={
                    !manualPandadocId ||
                    !data?.contract_third_party_info?.contract_id
                  }
                >
                  Save
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      {/* Render single action modals */}
      {single.modals}

      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(255,255,255,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Dialog>
  );
};
